import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { enquiry } from '../../../../helper/api_url';
import { swal } from '../../../../helper/swal';
import { setPreloader } from '../../../../features/Ui/uiSlice';
import { getEnquiryEffectiveStatus, getUniqueDistricts } from './enquiryUtils';

export const useEnquiryList = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const authData = useSelector((state) => state.auth._user);

    const [selectedDistrict, setSelectedDistrict] = useState('all');

    // Fetch enquiries using TanStack Query
    const {
        data: allEnquiries = [],
        isLoading,
        isError,
        error
    } = useQuery({
        queryKey: ['enquirys'],
        queryFn: enquiry.list,
        staleTime: 20 * 60 * 1000,
        cacheTime: 20 * 60 * 1000,
        select: (data) => data?.data?.enquiry || []
    });

    // Only active/pending enquiries shown here (completed and not interested have separate menus)
    const pendingEnquiries = useMemo(() => {
        return allEnquiries.filter(
            (item) => getEnquiryEffectiveStatus(item) === 'pending'
        );
    }, [allEnquiries]);

    // Extract unique sorted districts
    const districtList = useMemo(() => {
        return getUniqueDistricts(pendingEnquiries);
    }, [pendingEnquiries]);

    // Filter by selected district
    const displayedEnquiries = useMemo(() => {
        if (selectedDistrict === 'all') return pendingEnquiries;
        return pendingEnquiries.filter((item) => {
            const d = (item.district || '').trim().toLowerCase();
            return d === selectedDistrict.toLowerCase();
        });
    }, [pendingEnquiries, selectedDistrict]);

    // Single error handler (avoid duplicate execution)
    useEffect(() => {
        if (isError && error) {
            swal.error(error.response?.data?.message || error.message || 'Failed to fetch enquiries');
        }
    }, [isError, error]);

    // Handle enquiry deletion with confirmation
    const handleEnquiryDelete = useCallback((id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this enquiry!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(setPreloader({ loader: true, message: 'Deleting enquiry, please wait...' }));
                enquiry
                    .delete(id)
                    .then(() => {
                        queryClient.invalidateQueries(['enquirys']);
                        swal.success('Enquiry deleted successfully');
                    })
                    .catch((err) => {
                        swal.error(err.response?.data?.message || err.message || 'Failed to delete enquiry');
                    })
                    .finally(() => {
                        dispatch(setPreloader({ loader: false, message: '' }));
                    });
            }
        });
    }, [dispatch, queryClient]);

    const handleReport = useCallback((data) => {
        navigate('/report', { state: { data } });
    }, [navigate]);

    return {
        enquirys: displayedEnquiries,
        rawEnquiries: pendingEnquiries,
        districtList,
        selectedDistrict,
        setSelectedDistrict,
        isLoading,
        authData,
        handleEnquiryDelete,
        handleReport
    };
};

export default useEnquiryList;
